// shared-components/features/announcements/AnnouncementBanner.jsx

import { useState, useEffect, useRef, useCallback } from "react";
import { X } from "lucide-react";
import { storage } from "@shared/utils";
import { announcementService } from "@shared/services";
import { useWebsite, useSiteGradients } from "@shared/hooks";

export const AnnouncementBanner = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const { websiteId } = useWebsite();
  const lastCheckRef = useRef(new Date());
  const gradients = useSiteGradients();

  // Use a ref to store dismissed IDs so they're always current
  const dismissedRef = useRef(
    JSON.parse(storage.getItem("dismissed-announcements") || "[]"),
  );

  const fetchAnnouncements = useCallback(async () => {
    try {
      announcementService.setWebsiteId(websiteId);
      const data = await announcementService.getActive();

      // Use the ref which always has the current value
      const filtered = data.filter((a) => !dismissedRef.current.includes(a.id));
      setAnnouncements(filtered);

      // Update last check time
      lastCheckRef.current = new Date();
    } catch (error) {
      console.error("Error fetching announcements:", error);
    } finally {
      setLoading(false);
    }
  }, [websiteId]);

  // Initial fetch
  useEffect(() => {
    if (websiteId) {
      fetchAnnouncements();
    }
  }, [websiteId, fetchAnnouncements]);

  // Set up polling and focus listener
  useEffect(() => {
    if (websiteId) {
      // Set up polling for new announcements every 30 seconds
      const interval = setInterval(() => {
        fetchAnnouncements();
      }, 30000);

      // Also check when window regains focus
      const handleFocus = () => {
        // Only fetch if it's been more than 5 seconds since last check
        const timeSinceLastCheck = new Date() - lastCheckRef.current;
        if (timeSinceLastCheck > 5000) {
          fetchAnnouncements();
        }
      };

      window.addEventListener("focus", handleFocus);

      return () => {
        clearInterval(interval);
        window.removeEventListener("focus", handleFocus);
      };
    }
  }, [websiteId, fetchAnnouncements]);

  // Auto-rotate through announcements
  useEffect(() => {
    if (announcements.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % announcements.length);
      }, 5000); // Rotate every 5 seconds

      return () => clearInterval(interval);
    }
  }, [announcements.length]);

  const handleDismiss = () => {
    const current = announcements[currentIndex];
    if (current && current.is_dismissible) {
      // Update dismissed list in ref
      dismissedRef.current = [...dismissedRef.current, current.id];

      // Update storage
      storage.setItem(
        "dismissed-announcements",
        JSON.stringify(dismissedRef.current),
      );

      // Remove from current announcements immediately
      const filtered = announcements.filter((a) => a.id !== current.id);
      setAnnouncements(filtered);

      // Reset index if needed
      if (currentIndex >= filtered.length && filtered.length > 0) {
        setCurrentIndex(0);
      } else if (filtered.length === 0) {
        setCurrentIndex(0);
      }
    }
  };

  // Development helper to clear dismissed announcements
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Ctrl+Shift+A to clear dismissed announcements (dev only)
      if (e.ctrlKey && e.shiftKey && e.key === "A") {
        storage.removeItem("dismissed-announcements");
        dismissedRef.current = [];
        fetchAnnouncements(); // Refetch to show all announcements
      }
    };

    if (import.meta.env.DEV) {
      window.addEventListener("keydown", handleKeyPress);
      return () => window.removeEventListener("keydown", handleKeyPress);
    }
  }, [fetchAnnouncements]);

  if (loading || announcements.length === 0) return null;

  const current = announcements[currentIndex];
  if (!current) return null;

  // Style variants
  const getStyleClass = (style) => {
    const announcementGradients = {
      info: gradients.announcementsInfo,
      success: gradients.announcementsSuccess,
      warning: gradients.announcementsWarning,
      urgent: gradients.announcementsUrgent,
    };

    return `${announcementGradients[style] || gradients.announcementsInfo} border-b`;
  };

  return (
    <div
      className={`
      relative border-b px-4 py-3
      ${getStyleClass(current.style)}
      transition-all duration-300
    `}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex-1 flex items-center gap-4">
          <div className="flex-1">
            <span className="font-medium">{current.title}</span>
            {current.message && (
              <span className="ml-2 opacity-90">{current.message}</span>
            )}
            {current.link_url && (
              <a
                href={current.link_url}
                className="ml-3 underline hover:no-underline font-medium"
              >
                {current.link_text || "Learn More"} →
              </a>
            )}
          </div>

          {announcements.length > 1 && (
            <div className="flex items-center gap-1">
              {announcements.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`
                    w-2 h-2 rounded-full transition-all
                    ${
                      index === currentIndex
                        ? "bg-current w-6"
                        : "bg-current opacity-30"
                    }
                  `}
                  aria-label={`Go to announcement ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {current.is_dismissible && (
          <button
            onClick={handleDismiss}
            className="ml-4 p-1 hover:opacity-70 transition-opacity"
            aria-label="Dismiss announcement"
          >
            <X size={20} />
          </button>
        )}
      </div>
    </div>
  );
};
